import React from "react";
import { styled } from "@mui/material/styles";
import { IconButton, TextField, TextFieldProps } from "@mui/material";
import { CustomFormLabel } from "./CustomFormLabel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export const CustomTextField = styled((props: TextFieldProps) => (
  <TextField {...props} />
))(({ theme }) => ({
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& .MuiOutlinedInput-input": {
    padding: "5px 8px",
    fontSize: "0.9rem",
    color: "rgb(38, 38, 38)",
    [theme.breakpoints.up("md")]: {                
      padding: "9px 8px",
      fontSize: "0.9rem",
    },
    [theme.breakpoints.up("lg")]: {
      padding: "8px 10px",
      fontSize: "0.9rem",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "9px 13px!important",
    },
  },

  "& .MuiOutlinedInput-input::-webkit-input-placeholder": {
    color: "#767e89",
    opacity: "1",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: `${
      theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#dee3e9"
    }`,
    borderRadius: "5px",
  },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    backgroundColor: `${
      theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12) " : "#f8f9fb "
    }`,
  },
  "& .MuiOutlinedInput-input.Mui-disabled::-webkit-input-placeholder": {
    color: "#767e89",
    opacity: "1",
  },
 
}));


export const CustomShrinkTextField = styled((props: TextFieldProps) => {
  // Extract value from TextField props or InputProps (used by Autocomplete)
  const actualValue =
    props.value ??
    props.inputProps?.value ??
    props.InputProps?.value ??
    "";

  return (
    <TextField
      {...props}
      InputLabelProps={{
        ...props.InputLabelProps,
        shrink: actualValue !== "" && actualValue !== undefined,
      }}
    />
  );
})(({ theme, size }) => ({
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },

  "& .MuiInputLabel-shrink": {
    fontSize: size === "small" ? "1.1rem" : "1.3rem",
    fontWeight: 600,
  },

  "& .MuiInputLabel-shrink + .MuiOutlinedInput-root fieldset legend": {
    maxWidth: "100%",
    fontSize: size === "small" ? "0.85rem" : "1.05rem",
  },
}));

interface CustomPasswordFieldProps extends Omit<TextFieldProps, "value"> {
  readOnlyMode?: boolean;
  value?: string | number;
  heading?: string;
  showPassword?: boolean;
  onShowPasswordChange?: (show: boolean) => void;
}

export const CustomPasswordField: React.FC<CustomPasswordFieldProps> = (
  props
) => {
  const {
    value,
    onChange,
    heading,
    showPassword: externalShowPassword,
    onShowPasswordChange,
    ...rest
  } = props;
  const [internalShowPassword, setInternalShowPassword] = React.useState(false);

  // Use external showPassword if provided, otherwise use internal state
  const showPassword =
    externalShowPassword !== undefined
      ? externalShowPassword
      : internalShowPassword;

  const handleClickShowPassword = () => {
    const newShowPassword = !showPassword;
    if (onShowPasswordChange) {
      onShowPasswordChange(newShowPassword);
    } else {
      setInternalShowPassword(newShowPassword);
    }
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <>
      {heading && <CustomFormLabel>{heading}</CustomFormLabel>}
      <CustomTextField
        {...rest}
        value={value}
        onChange={onChange}
        type={showPassword ? "text" : "password"}
        InputProps={{
          ...rest.InputProps,
          endAdornment: (
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
              sx={{
                color: showPassword ? "primary.main" : "grey.500",
                "&:hover": {
                  color: showPassword ? "primary.dark" : "grey.700",
                },
              }}
            >
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          ),
        }}
      />
    </>
  );
};
